import React, { useState, useEffect } from 'react';
import { Linkedin, Mail, Moon, Sun, Menu, X, LayoutGrid, Database, Lock, Puzzle, Paintbrush, Palette, Code, Layers, Zap, Terminal, Box } from 'lucide-react';
import ContactForm from './ContactForm.tsx';
import Seo from './Seo.tsx';

/**
 * Landing page for Gutenberg / WordPress Block Editor development services.
 * Targets agency decision-makers and technical leads who need custom block development.
 * Positions Ben as a React developer who builds Gutenberg blocks, not a WordPress dev.
 */
const GutenbergPage: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('darkMode');
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (stored === 'true' || stored === 'false') {
      setIsDarkMode(stored === 'true');
    } else {
      setIsDarkMode(mediaQuery.matches);
    }
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    const newValue = !isDarkMode;
    setIsDarkMode(newValue);
    localStorage.setItem('darkMode', newValue.toString());
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const services = [
    {
      icon: <LayoutGrid size={24} />,
      title: 'Custom Content Blocks',
      description: 'Purpose-built blocks for your specific content types. Hero sections, testimonials, feature grids, team profiles, CTAs. Designed to feel native to WordPress.'
    },
    {
      icon: <Database size={24} />,
      title: 'Dynamic Blocks',
      description: 'Blocks that pull live data: latest posts, events, API integrations, filtered content feeds. Server-side rendered with PHP, cached for performance.'
    },
    {
      icon: <Lock size={24} />,
      title: 'Editorial Workflows',
      description: 'Block patterns, locked templates, and content-only editing modes that protect layout integrity while giving editors freedom where it matters.'
    },
    {
      icon: <Puzzle size={24} />,
      title: 'Block Editor Extensions',
      description: 'Custom sidebar panels, toolbar controls, and editor plugins that add functionality to the editing experience itself, not just the page content.'
    },
    {
      icon: <Paintbrush size={24} />,
      title: 'Existing Block Customization',
      description: 'Extending core blocks with custom styles, variations, and additional controls. Add functionality to what WordPress already provides without reinventing the wheel.'
    },
    {
      icon: <Palette size={24} />,
      title: 'theme.json Integration',
      description: 'Design system configuration that connects your blocks to a unified color palette, typography scale, and spacing system. Editors pick from your approved options.'
    }
  ];

  const technicalCategories = [
    {
      icon: <Box size={24} />,
      title: 'Block API',
      items: [
        'block.json API v3 registration',
        'Edit/Save component architecture',
        'Static and dynamic (PHP-rendered) blocks',
        'Block deprecations and content migration',
        'Block variations, transforms, and styles',
        'Block patterns and pattern categories',
        'InnerBlocks with template locking',
        'Parent/ancestor block relationships',
        'Block hooks (WP 6.5+)'
      ]
    },
    {
      icon: <Layers size={24} />,
      title: 'Editor Integration',
      items: [
        '@wordpress/data custom Redux stores',
        'useSelect / useDispatch for cross-block communication',
        'InspectorControls, BlockControls, custom SlotFills',
        'JS filters and hooks (@wordpress/hooks)',
        'Higher-order components for extending core blocks',
        'registerPlugin for custom sidebar panels',
        'PluginDocumentSettingPanel, PluginPostStatusInfo'
      ]
    },
    {
      icon: <Zap size={24} />,
      title: 'Rendering & Performance',
      items: [
        'Server-side rendering with render.php',
        'Fragment caching with transients',
        'Optimized WP_Query (no_found_rows, selective cache priming)',
        'get_block_wrapper_attributes() for consistent output'
      ]
    },
    {
      icon: <Code size={24} />,
      title: 'Modern WordPress',
      items: [
        'Interactivity API (WP 6.5+) for lightweight frontend interactivity',
        'theme.json v3 design system integration',
        'Full Site Editing compatibility',
        'wp-scripts build toolchain'
      ]
    },
    {
      icon: <Terminal size={24} />,
      title: 'Core Stack',
      items: [
        'React (hooks, context, component architecture)',
        'TypeScript / JavaScript',
        'PHP (OOP, autoloading, REST API endpoints)',
        'WCAG 2.0+ AA / Section 508 accessibility'
      ]
    }
  ];

  return (
    <div className={`min-h-screen bg-neutral-50 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 font-sans ${isLoaded ? 'animate-fadeInUp' : 'opacity-0'}`}>
      <Seo
        title="Custom Gutenberg Block Developer | Ben Smith"
        description="Freelance React developer specializing in custom Gutenberg blocks and WordPress Block Editor extensions for agencies and enterprise teams."
        path="/gutenberg"
      />

      {/* Navigation */}
      <nav className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md py-4 px-6 sticky top-0 shadow-soft z-50 border-b border-primary-100 dark:border-neutral-700">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <a href="/" className="text-2xl font-bold">
            <span className="text-neutral-900 dark:text-neutral-100">Ben Smith</span>
            <span className="text-primary-600 dark:text-primary-400"> Development</span>
          </a>
          <div className="hidden md:flex space-x-8 items-center">
            <a href="/" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Home
            </a>
            <button onClick={() => scrollToSection('services')} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Services
            </button>
            <button onClick={() => scrollToSection('experience')} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Experience
            </button>
            <a href="/about" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              About
            </a>
            <button onClick={() => scrollToSection('contact')} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Contact
            </button>
            <button
              onClick={toggleDarkMode}
              className="transition-all duration-200 font-medium hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-3 py-1.5"
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
          <button
            className="md:hidden p-2 hover:bg-primary-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={24} className="text-neutral-700 dark:text-neutral-300" /> : <Menu size={24} className="text-neutral-700 dark:text-neutral-300" />}
          </button>
        </div>
        {isMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border-b border-primary-100 dark:border-neutral-700 p-4 shadow-soft rounded-b-2xl z-50">
            <div className="flex flex-col space-y-4">
              <a href="/" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>Home</a>
              <button onClick={() => scrollToSection('services')} className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md">Services</button>
              <button onClick={() => scrollToSection('experience')} className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md">Experience</button>
              <a href="/about" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>About</a>
              <button onClick={() => scrollToSection('contact')} className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md">Contact</button>
            </div>
          </div>
        )}
      </nav>

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-primary-900 text-white py-28 md:py-36 px-6 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>

          <div className="relative max-w-5xl mx-auto z-10">
            <div className="inline-block mb-6 px-4 py-1.5 bg-primary-500/20 border border-primary-400/30 rounded-full">
              <span className="text-primary-300 text-sm font-medium tracking-wide uppercase">Gutenberg development</span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
              Custom Gutenberg Blocks.<br />
              <span className="text-primary-400">Built by a React Developer.</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-2xl mb-6 text-neutral-300 leading-relaxed">
              Custom Block Editor experiences for your content team. No page builders. No fragile workarounds. No developer needed to update a page.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={() => scrollToSection('contact')} className="bg-primary-600 hover:bg-primary-500 text-white font-semibold py-3.5 px-8 rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300 text-center">
                Let's talk
              </button>
              <button onClick={() => scrollToSection('services')} className="bg-transparent border-2 border-white/30 text-white font-semibold py-3.5 px-8 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 text-center">
                See what I build
              </button>
            </div>
          </div>
        </section>

        {/* The Problem */}
        <section className="py-24 px-6 bg-white dark:bg-neutral-800">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-primary-700 dark:text-gray-100">
              Your editors shouldn't need a developer to update content.
            </h2>
            <div className="space-y-6 text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed">
              <p>
                Most WordPress sites end up in one of two places: editors locked into rigid templates who can't change anything without filing a ticket, or editors given so much freedom that they break the design every time they touch a page.
              </p>
              <p>
                Custom Gutenberg blocks solve both problems. Purpose-built editing tools that look and feel native to WordPress, protect the design system, and let non-technical teams publish confidently. That's what I build.
              </p>
            </div>
          </div>
        </section>

        {/* Block Development Services */}
        <section id="services" className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center text-primary-700 dark:text-gray-100">
              Block development services
            </h2>
            <p className="text-lg text-neutral-500 dark:text-neutral-400 text-center mb-16 max-w-2xl mx-auto">
              Everything your content team needs to publish confidently, built directly into the Block Editor.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <div key={index} className="bg-white dark:bg-neutral-800 p-6 rounded-2xl shadow-soft border border-neutral-200 dark:border-neutral-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="p-3 bg-primary-100 dark:bg-primary-900/40 rounded-xl w-fit mb-4 text-primary-600 dark:text-primary-400">
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-neutral-900 dark:text-neutral-100">{service.title}</h3>
                  <p className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Experience */}
        <section id="experience" className="py-24 px-6 bg-white dark:bg-neutral-800">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-primary-700 dark:text-gray-100">
              Enterprise block development at scale
            </h2>
            <div className="space-y-6 text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed">
              <p>
                At KHM Agency, I build custom Gutenberg blocks and Block Editor extensions for major destination marketing platforms. Sites like Discover Atlanta, The Palm Beaches, Buffalo Niagara, and others. These are content-heavy sites where non-technical marketing teams need to build complex destination pages with embedded maps, event feeds, curated content sections, and more.
              </p>
              <p>
                I built the blocks that let them do it. No developer bottleneck. Just clean, purpose-built editorial tools across 10+ regional platforms.
              </p>
              <p>
                Before that, 15 years of WordPress development across 150+ projects. Custom plugins, themes, REST API integrations, and full-stack builds for clients ranging from Cisco to the National Park Service.
              </p>
            </div>
          </div>
        </section>

        {/* Under the Hood - Technical Depth */}
        <section id="technical" className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center text-primary-700 dark:text-gray-100">
              Under the hood
            </h2>
            <p className="text-lg text-neutral-500 dark:text-neutral-400 text-center mb-16 max-w-2xl mx-auto">
              The real Block API. Not the YouTube tutorial version.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {technicalCategories.map((category, index) => (
                <div key={index} className={`bg-white dark:bg-neutral-800 p-6 rounded-2xl shadow-soft border border-neutral-200 dark:border-neutral-700 ${index === technicalCategories.length - 1 && technicalCategories.length % 2 !== 0 ? 'md:col-span-2 md:max-w-[calc(50%-12px)] md:mx-auto' : ''}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-primary-100 dark:bg-primary-900/40 rounded-xl text-primary-600 dark:text-primary-400">
                      {category.icon}
                    </div>
                    <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">{category.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {category.items.map((item, i) => (
                      <li key={i} className="text-sm text-neutral-600 dark:text-neutral-400 flex items-start gap-2">
                        <span className="text-primary-500 dark:text-primary-400 mt-1.5 flex-shrink-0 w-1 h-1 rounded-full bg-primary-500 dark:bg-primary-400"></span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who This Is For */}
        <section id="audience" className="py-24 px-6 bg-white dark:bg-neutral-800">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-primary-700 dark:text-gray-100">
              Built for agencies and teams
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed mb-10">
              I work best as a technical partner for agencies and organizations that need custom block development but don't have a dedicated Gutenberg specialist in-house. You handle the design and client relationship. I handle the block builds.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                'An agency building WordPress sites where the editorial experience matters',
                'A publisher or media company that needs custom newsroom tools in the block editor',
                'A university, nonprofit, or government org with non-technical content editors',
                'A WordPress shop that needs to level up from plugins to native blocks'
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-neutral-50 dark:bg-neutral-700/30 rounded-xl">
                  <div className="mt-0.5 w-2 h-2 rounded-full bg-primary-500 dark:bg-primary-400 flex-shrink-0"></div>
                  <p className="text-neutral-700 dark:text-neutral-300 text-sm leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-24 px-6 bg-primary-50 dark:bg-neutral-900">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center text-primary-700 dark:text-gray-100">Let's build something.</h2>
            <p className="text-lg text-neutral-500 dark:text-neutral-400 text-center mb-12 max-w-2xl mx-auto">
              Have a project that needs custom block development? I'd like to hear about it.
            </p>
            <div className="bg-white dark:bg-neutral-800 p-8 rounded-2xl shadow-soft max-w-2xl mx-auto border border-primary-100 dark:border-neutral-700">
              <ContactForm />
            </div>
            <div className="text-center mt-12">
              <p className="text-lg dark:text-neutral-200">Or email me directly:</p>
              <a href="mailto:ben@bensmithdev.com" className="text-primary-600 dark:text-primary-400 font-semibold hover:text-accent-600 dark:hover:text-accent-400 transition-colors duration-200">ben@bensmithdev.com</a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-neutral-900 dark:bg-black text-white py-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="mb-6 md:mb-0">
                <a href="/" className="text-2xl font-bold">
                  <span className="text-white">Ben Smith</span>
                  <span className="text-orange-500"> Development</span>
                </a>
                <p className="mt-2 text-primary-200 dark:text-primary-300">Custom Gutenberg blocks and Block Editor development for agencies.</p>
              </div>
              <div className="flex space-x-6">
                <a
                  href="https://www.linkedin.com/in/benjaminchristophersmith/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-300 dark:text-primary-400 hover:text-accent-300 dark:hover:text-accent-400 transition-all duration-200 hover:scale-110 rounded-full p-2"
                  aria-label="LinkedIn Profile"
                >
                  <Linkedin size={24} />
                </a>
                <a
                  href="mailto:ben@bensmithdev.com"
                  className="text-primary-300 dark:text-primary-400 hover:text-accent-300 dark:hover:text-accent-400 transition-all duration-200 hover:scale-110 rounded-full p-2"
                  aria-label="Email ben@bensmithdev.com"
                >
                  <Mail size={24} />
                </a>
              </div>
            </div>
            <div className="border-t border-primary-700/30 dark:border-primary-600/30 mt-8 pt-8 text-center text-primary-300 dark:text-primary-400">
              <p>&copy; {new Date().getFullYear()} Benjamin Smith. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default GutenbergPage;
